import { Box, Popover, Typography } from "@mui/material";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import CustomTooltip from "../../../components/CustomTooltip";
import InfoCalculationButton from "../../../components/InfoCalculationButton";
import SvgToIcon from "../../../icons/SvgToIcon";
import CustomMenu from "../../settings/inbox/MoreInfoMenu";
import { icons, severities } from "../../stack/environment/icons/iconExport";

export default function WidgetWrapper({
  title,
  viewAll = {
    handleViewAll: null,
    title: "View all",
    tooltip: "",
    newTab: false,
    hideIcon: false,
  },
  sortMenuItems = { data: [], tooltip: "" },
  handleExportToPdf,
  onExport,
  subTitle = "",
  info,
  calculationInfo = null,
  children,
}) {
  const colors = useColors();
  const [tileHover, setTileHover] = useState(null);
  const { trackEvent } = useAPI();
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [calculationInfoAnchorEl, setCalculationInfoAnchorEl] = useState(null);
  const handleClickFilterMenu = (event) => {
    trackEvent("User clicked on filter menu in widget " + title);
    setAnchorElFilter(event.currentTarget);
  };
  const styles = {
    widget: {
      backgroundColor: "white",
      boxShadow: `0px 0px 2px 1px ${colors.grey_24percent}`,
      borderRadius: "12px",
      overflow: "hidden",
      height: "100%",
    },
    widgetHeader: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "4px",
      padding: "12px 8px 0px 4px",
      cursor: "grab",
    },
    widgetHeaderTitle: {
      display: "flex",
      flexDirection: "row",
      gap: "4px",
      alignItems: "center",
    },
    widgetTitle: {
      margin: 0,
      fontSize: "14px",
    },
    container: {
      height: "100%",
      backgroundColor: colors.common_white,
      boxShadow: `0px 0px 2px 1px ${colors.grey_24percent}`,
      borderRadius: "12px",
      overflow: "hidden",
    },
    content: {
      height: "calc(100% - 45px)",
      overflow: "auto",
    },
  };

  return (
    <Box sx={styles.container}>
      <Box
        style={styles.widgetHeader}
        className="widgetHeader"
        onMouseEnter={() => {
          setTileHover(title);
        }}
        onMouseLeave={() => setTileHover(null)}
      >
        <Box className="widgetHeaderTitle" style={styles.widgetHeaderTitle}>
          <SvgToIcon
            IconLogo={icons.DragIcon}
            style={{
              color: colors.grey_500,
              width: "16px",
              visibility: tileHover === title ? "visible" : "hidden",
            }}
          />
          <Typography style={styles.widgetTitle} variant="subtitle1">
            {title}
          </Typography>
          {info && (
            <CustomTooltip
              title={info}
              placement="top"
              onMouseEnter={() => {
                trackEvent(`user over on info Widget ${title}`);
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgToIcon
                  IconLogo={severities.SeverityInfoTooltipIcon}
                  style={{
                    width: "16px",
                  }}
                />
              </Box>
            </CustomTooltip>
          )}
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            paddingRight: "16px",
          }}
        >
          {calculationInfo && (
            <>
              <InfoCalculationButton
                label={calculationInfo.label}
                anchorEl={calculationInfoAnchorEl}
                setAnchorEl={setCalculationInfoAnchorEl}
              />
              <Popover
                id="heatmap-score-calculation-popover"
                open={Boolean(calculationInfoAnchorEl)}
                anchorEl={calculationInfoAnchorEl}
                onClose={() => setCalculationInfoAnchorEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  sx: {
                    p: 3,
                    maxWidth: "650px",
                    width: "650px",
                    borderRadius: "10px",
                    boxShadow: "1px 1px 8.5px 3px rgba(203, 203, 203, 0.50)",
                  },
                }}
                disableRestoreFocus
              >
                <Box>
                  {typeof calculationInfo.content === "string" ? (
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      {calculationInfo.content}
                    </Typography>
                  ) : (
                    calculationInfo.content
                  )}
                </Box>
              </Popover>
            </>
          )}
          {sortMenuItems.data.length > 0 && (
            <Box>
              <CustomTooltip title={sortMenuItems.tooltip} placement="top">
                <Box
                  onClick={handleClickFilterMenu}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    width: "32px",
                    height: "32px",
                    justifyContent: "center",
                  }}
                >
                  <SvgToIcon
                    IconLogo={icons.FilterIcon}
                    style={{ width: "18px", height: "18px" }}
                  />
                </Box>
              </CustomTooltip>

              <CustomMenu
                title={"View options"}
                anchorElMenu={anchorElFilter}
                openMenu={Boolean(anchorElFilter)}
                handleCloseMenu={() => {
                  setAnchorElFilter(null);
                }}
                moreActions={sortMenuItems.data}
              />
            </Box>
          )}
          {handleExportToPdf && (
            <CustomTooltip title="Export to PDF" placement="top">
              <Box
                onClick={handleExportToPdf}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  width: "32px",
                  height: "32px",
                  justifyContent: "center",
                }}
              >
                <SvgToIcon
                  IconLogo={icons.ExportPDFIcon}
                  style={{ width: "18px", height: "18px" }}
                />
              </Box>
            </CustomTooltip>
          )}
          {onExport && (
            <CustomTooltip title="Export to CSV" placement="top">
              <Box
                onClick={onExport}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "32px",
                  height: "32px",
                  justifyContent: "center",
                }}
              >
                <SvgToIcon
                  IconLogo={icons.ExportCSVIcon}
                  style={{ width: "18px", height: "18px" }}
                />
              </Box>
            </CustomTooltip>
          )}
          {viewAll.handleViewAll && (
            <CustomTooltip title={viewAll.tooltip} placement="top">
              <Box
                onClick={viewAll.handleViewAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  height: "32px",
                  padding: "0px 4px",
                }}
              >
                <Typography variant="caption">
                  {viewAll.title || "View all"}
                </Typography>
                {!viewAll.hideIcon && (
                  <SvgToIcon
                    IconLogo={viewAll.newTab ? icons.NewTabIcon : icons.arrow}
                    style={{ width: "18px" }}
                  />
                )}
              </Box>
            </CustomTooltip>
          )}
        </Box>
      </Box>
      {subTitle && (
        <Typography
          sx={{ ml: 3 }}
          color={colors.text_secondary}
          variant="caption"
        >
          {subTitle}
        </Typography>
      )}
      <Box sx={styles.content}>{children}</Box>
    </Box>
  );
}
